// Formular "Neuer Auftrag" des Dokumenten-Assistenten (seit 21.10.2026).
//
// Drei Schritte: Deal, Vertragsart, Dokument. Das Dokument ist entweder eine
// Datei (PDF/JPG/PNG), die in Teilen zu hoechstens 3 MB Base64 hochgeht
// (op "teil", danach "starten"), oder der Vertrag, der schon im Deal liegt
// ("Vertrag aus dem Deal holen" -> op "vertrag-aus-deal", dann "starten" mit
// quelle "deal"; der Browser bekommt dabei keine Bytes zu sehen).

import React, { useState } from 'react';
import { toast } from 'sonner';
import { CloudDownload, FileText, Plus, X as XIcon } from 'lucide-react';
import { T, feldStil, karteStil, knopfStil, primaerKnopfStil } from '../../../lib/designTokens';
import {
  VERTRAGSARTEN, fehlerText, formularFehler, groesseText, ladeDateiInTeilenHoch, neueUploadId,
  normalisiereDealId, startKoerper, teilBereiche, vertragsartLabel, vertragsartNachDealFund,
} from '../../../lib/dokumentenAssistent';
import { Ueberzeile } from '../abwaegenAnzeige';
import { Hinweis } from './AuftragDetails';
import DateiFeld from './DateiFeld';

const LAEUFT_TEXT = {
  deal: 'Sucht den Vertrag im Deal …',
  upload: 'Überträgt die Datei …',
  start: 'Legt den Auftrag an …',
};

function DealFund({ fund, gesperrt, onVerwerfen }) {
  return (
    <div style={{
      border: `1px solid ${T.gutRand}`, background: T.gutGrund, borderRadius: T.rKarte,
      padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
    }}>
      <FileText size={20} color={T.gut} style={{ flexShrink: 0 }} />
      <div style={{ flex: '1 1 240px', minWidth: 0 }}>
        <div style={{ fontSize: T.tText, fontWeight: 700, color: T.ink, wordBreak: 'break-all' }}>
          {fund.dateiname || 'Vertrag im Deal'}
        </div>
        <div style={{ fontSize: T.tMikro, color: T.body, lineHeight: 1.5 }}>
          {vertragsartLabel(fund.vertragsart)}-Vertrag aus Deal {fund.dealId}
          {fund.groesse ? ` · ${groesseText(fund.groesse)}` : ''} · wird direkt aus Bitrix gelesen
        </div>
      </div>
      <button type="button" onClick={onVerwerfen} disabled={gesperrt} style={knopfStil(false)}>
        <XIcon size={11} /> Stattdessen Datei hochladen
      </button>
    </div>
  );
}

function VertragsartWahl({ wert, gesperrt, onWahl }) {
  const gewaehlt = VERTRAGSARTEN.find((art) => art.wert === wert);
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <div role="radiogroup" aria-label="Vertragsart" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {VERTRAGSARTEN.map((art) => (
          <button
            key={art.wert} type="button" role="radio" aria-checked={wert === art.wert}
            onClick={() => onWahl(art.wert)} disabled={gesperrt}
            style={{ ...knopfStil(wert === art.wert), padding: '7px 12px', fontSize: T.tSm }}
          >
            {art.label}
          </button>
        ))}
      </div>
      {gewaehlt?.hinweis ? (
        <div style={{ fontSize: T.tMikro, color: T.orangeTinte, fontWeight: 600 }}>{gewaehlt.label}: {gewaehlt.hinweis}.</div>
      ) : null}
    </div>
  );
}

function Uebertragung({ teile }) {
  const anteil = teile.gesamt ? Math.round((teile.fertig / teile.gesamt) * 100) : 0;
  return (
    <div style={{ display: 'grid', gap: 4, minWidth: 180, flex: '1 1 180px' }}>
      <div style={{ fontSize: T.tMikro, color: T.body, fontWeight: 600 }}>
        Teil {teile.fertig} von {teile.gesamt} übertragen
      </div>
      <div
        role="progressbar" aria-valuemin={0} aria-valuemax={teile.gesamt} aria-valuenow={teile.fertig}
        aria-label={`Teil ${teile.fertig} von ${teile.gesamt} übertragen`}
        style={{ height: 6, borderRadius: 999, background: T.hairline, overflow: 'hidden' }}
      >
        <div style={{ width: `${anteil}%`, height: '100%', background: T.orange, transition: 'width 200ms ease' }} />
      </div>
    </div>
  );
}

/**
 * sende(koerper, timeout) schickt an /automation/dokumenten-assistent; onAngelegt(auftrag).
 * festeDealId: Reiter im Bitrix-Deal - der Deal steht fest und bleibt nach dem Anlegen stehen.
 */
export default function AuftragFormular({ sende, onAngelegt, festeDealId = '' }) {
  const [dealEingabe, setDealEingabe] = useState(festeDealId || '');
  const [vertragsart, setVertragsart] = useState('');
  const [datei, setDatei] = useState(null);
  const [dealFund, setDealFund] = useState(null);
  const [nurAnalyse, setNurAnalyse] = useState(false);
  const [laeuft, setLaeuft] = useState('');
  const [teile, setTeile] = useState(null);
  const [fehler, setFehler] = useState('');

  const dealId = normalisiereDealId(dealEingabe);
  // Ein Fund gilt nur fuer den Deal, in dem er gesucht wurde.
  const fundGilt = Boolean(dealFund && dealFund.dealId === dealId);
  const quelle = fundGilt ? 'deal' : 'upload';
  const gesperrt = Boolean(laeuft);
  // Was noch fehlt, steht ruhig neben dem Knopf - statt eines grauen Knopfs ohne Grund.
  const fehlt = formularFehler({ dealId, vertragsart, quelle, datei });
  const bereit = !gesperrt && !fehlt;

  const dateiWaehlen = (neu) => {
    setDatei(neu);
    if (neu) setDealFund(null);
    setFehler('');
  };

  const holeAusDeal = async () => {
    if (!dealId) {
      setFehler(formularFehler({ dealId }));
      return;
    }
    setLaeuft('deal');
    setFehler('');
    try {
      const { data } = await sende({ op: 'vertrag-aus-deal', dealId }, 60000);
      setDealFund({ dealId, vertragsart: data?.vertragsart, dateiname: data?.dateiname || '', groesse: data?.groesse || 0 });
      setVertragsart((alt) => vertragsartNachDealFund(alt, data?.vertragsart));
      setDatei(null);
    } catch (f) {
      setDealFund(null);
      setFehler(fehlerText(f, 'Der Vertrag konnte nicht aus dem Deal geholt werden.'));
    } finally {
      setLaeuft('');
    }
  };

  const zuruecksetzen = () => {
    setDealEingabe(festeDealId || '');
    setVertragsart('');
    setDatei(null);
    setDealFund(null);
    setNurAnalyse(false);
  };

  const hinzufuegen = async () => {
    const vorab = formularFehler({ dealId, vertragsart, quelle, datei });
    if (vorab) {
      setFehler(vorab);
      return;
    }
    setFehler('');
    let schritt = quelle === 'upload' ? 'upload' : 'start';
    try {
      let koerper = startKoerper({ dealId, vertragsart, quelle, nurAnalyse });
      if (quelle === 'upload') {
        const uploadId = neueUploadId();
        setLaeuft('upload');
        setTeile({ fertig: 0, gesamt: teilBereiche(datei.size).length });
        const anzahl = await ladeDateiInTeilenHoch({
          datei,
          uploadId,
          sendeTeil: (k) => sende(k, 90000),
          beiFortschritt: (fertig, gesamt) => setTeile({ fertig, gesamt }),
        });
        koerper = startKoerper({ dealId, vertragsart, quelle, uploadId, teile: anzahl, datei, nurAnalyse });
      }
      schritt = 'start';
      setLaeuft('start');
      const { data } = await sende(koerper, 60000);
      if (!data?.auftrag?.id) throw new Error('Der Server hat keinen Auftrag zurückgegeben.');
      onAngelegt(data.auftrag);
      toast.success(`Auftrag für Deal ${dealId} angelegt${nurAnalyse ? ' (Probelauf)' : ''}.`);
      zuruecksetzen();
    } catch (f) {
      setFehler(fehlerText(f, schritt === 'upload'
        ? 'Die Datei konnte nicht übertragen werden.'
        : 'Der Auftrag konnte nicht angelegt werden.'));
    } finally {
      setLaeuft('');
      setTeile(null);
    }
  };

  return (
    <section aria-label="Neuer Auftrag" style={{ ...karteStil, padding: '16px 18px', display: 'grid', gap: 18 }}>
      <div style={{ fontSize: T.tPanel, fontWeight: 700, color: T.navy, letterSpacing: T.spurTitel }}>Neuer Auftrag</div>

      <div style={{ display: 'grid', gap: 7 }}>
        <Ueberzeile nummer="1">Deal</Ueberzeile>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            value={dealEingabe}
            onChange={(e) => { setDealEingabe(e.target.value); setFehler(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter' && dealId && !gesperrt) holeAusDeal(); }}
            placeholder="z. B. 12345 oder D_12345"
            aria-label="Bitrix-Deal-ID"
            inputMode="numeric"
            disabled={gesperrt}
            readOnly={Boolean(festeDealId)}
            title={festeDealId ? 'Der Deal dieses Bitrix-Reiters' : undefined}
            style={{ ...feldStil, width: 220 }}
          />
          <button type="button" onClick={holeAusDeal} disabled={gesperrt || !dealId}
            title="Nimmt den Vertrag, der im Deal schon liegt (PV- oder BESS-Vertragsfeld)"
            style={{ ...knopfStil(false), padding: '8px 12px', opacity: gesperrt || !dealId ? 0.6 : 1 }}>
            <CloudDownload size={13} />
            {laeuft === 'deal' ? LAEUFT_TEXT.deal : 'Vertrag aus dem Deal holen'}
          </button>
        </div>
        {dealEingabe.trim() && !dealId ? (
          <div style={{ fontSize: T.tMikro, color: T.gefahr, fontWeight: 600 }}>
            Eine Deal-ID besteht nur aus Ziffern, z. B. 12345 oder D_12345.
          </div>
        ) : null}
      </div>

      <div style={{ display: 'grid', gap: 7 }}>
        <Ueberzeile nummer="2">Vertragsart</Ueberzeile>
        <VertragsartWahl wert={vertragsart} gesperrt={gesperrt} onWahl={(wert) => { setVertragsart(wert); setFehler(''); }} />
      </div>

      <div style={{ display: 'grid', gap: 7 }}>
        <Ueberzeile nummer="3">Dokument</Ueberzeile>
        {fundGilt ? (
          <DealFund fund={dealFund} gesperrt={gesperrt} onVerwerfen={() => setDealFund(null)} />
        ) : (
          <DateiFeld datei={datei} onWahl={dateiWaehlen} gesperrt={gesperrt} />
        )}
      </div>

      <div style={{
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
        borderTop: `1px solid ${T.hairline}`, paddingTop: 14,
      }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: T.tSm, color: T.body, cursor: 'pointer' }}>
          <input type="checkbox" checked={nurAnalyse} onChange={(e) => setNurAnalyse(e.target.checked)} disabled={gesperrt} />
          Probelauf – nur erkennen, nichts hochladen
        </label>
        {teile ? <Uebertragung teile={teile} /> : (
          <span style={{ flex: '1 1 200px', fontSize: T.tMikro, color: T.muted, textAlign: 'right' }}>
            {gesperrt ? '' : fehlt}
          </span>
        )}
        <button type="button" onClick={hinzufuegen} disabled={!bereit}
          style={{ ...primaerKnopfStil, padding: '9px 16px', fontSize: T.tSm, opacity: bereit ? 1 : 0.45 }}>
          <Plus size={14} />
          {laeuft === 'upload' || laeuft === 'start' ? LAEUFT_TEXT[laeuft] : 'Hinzufügen'}
        </button>
      </div>

      {fehler ? <Hinweis ton="gefahr">{fehler}</Hinweis> : null}
    </section>
  );
}
